import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom, type Atom } from 'jotai';
import type { BrtNode, BunjaHookValue, ContextValuePair } from 'react-bunja-devtools/brt';
import { Inspector, chromeDark } from 'react-inspector';

import { snackbar } from '@flipster/react/design-system/snackbar';
import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';

import { brtNodeBunja } from '../state';

export const NodeDetails = () => {
  const { unselect, nodeAtom } = useBunja(brtNodeBunja);
  const node = useAtomValue(nodeAtom);
  return (
    <div
      className={stack({
        flex: 1,
        minH: 0,
        bgColor: 'background.default.level-0',
        borderLeft: '1px solid',
        borderLeftColor: 'border.default.level-1',
      })}
    >
      <div
        className={flex({
          h: 28,
          px: 8,
          borderBottom: '1px solid',
          borderBottomColor: 'border.default.level-1',
          bgColor: 'background.default.level-1',
          alignItems: 'center',
          justifyContent: 'space-between',
        })}
      >
        <div className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-5' })}>
          {node.type === 'context' ? `Context × ${node.contexts.length}` : node.name || '[Anonymous]'}
        </div>
        <button
          onClick={unselect}
          className={css({
            cursor: 'pointer',
            border: 'none',
            bgColor: 'transparent',
            color: 'content.default.level-4',
            lineHeight: 1,
            px: 8,
            py: 2,
            rounded: 3,
            transition: 'all 0.15s ease',
            _hover: {
              bgColor: 'background.default.level-2',
              color: 'content.default.level-5',
            },
          })}
          aria-label='Close'
        >
          ×
        </button>
      </div>
      <div className={css({ flex: 1, minH: 0, pb: '50vh', overflowY: 'scroll' })}>
        {node.type === 'context' ? <ContextDetails node={node} /> : <ComponentDetails node={node} />}
      </div>
    </div>
  );
};

interface NodeProps {
  node: BrtNode;
}

const ContextDetails = ({ node }: NodeProps) => {
  if (node.type !== 'context') return null;
  return (
    <div className={stack({ p: 8, gap: 8 })}>
      {node.contexts.map((pair, index) => (
        <ContextDetail key={index} pair={pair} />
      ))}
    </div>
  );
};

interface ContextDetailProps {
  pair: ContextValuePair;
}
const ContextDetail = ({ pair }: ContextDetailProps) => {
  const [context, value] = pair;
  const name = context.displayName || '[Anonymous]';
  const storeAsTmp = getStoreAsTmpFn(name, value);
  return (
    <div className={stack({ gap: 4, textStyle: 'Text/S/Monospace/Regular' })}>
      <div className={flex({ alignItems: 'center', justifyContent: 'space-between' })}>
        <span className={css({ color: 'content.default.level-5' })}>{name}</span>
        <StoreAsTmpButton onClick={storeAsTmp} />
      </div>
      <div className={css({ p: 8, rounded: 4 })} style={{ background: chromeDark.BASE_BACKGROUND_COLOR }}>
        <Inspector theme='chromeDark' table={false} data={value} />
      </div>
    </div>
  );
};

const ComponentDetails = ({ node }: NodeProps) => {
  if (node.type !== 'component') return null;
  return (
    <div className={stack({ p: 8, gap: 8 })}>
      {node.bunjaHooks.map((hookValue, index) => (
        <ComponentDetail key={index} bunjaHookValue={hookValue} />
      ))}
    </div>
  );
};

interface ComponentDetailProps {
  bunjaHookValue: BunjaHookValue;
}
const ComponentDetail = ({ bunjaHookValue }: ComponentDetailProps) => {
  const { bunja, bunjaInstance } = bunjaHookValue;
  const { id, value } = bunjaInstance as { id: string; value: unknown };
  const name = bunja.debugLabel || '[Anonymous]';
  const storeAsTmp = getStoreAsTmpFn(name, value);
  return (
    <div className={stack({ gap: 4, textStyle: 'Text/S/Monospace/Regular' })}>
      <div className={flex({ alignItems: 'center', justifyContent: 'space-between' })}>
        <span className={flex({ gap: 4, color: 'content.default.level-5' })}>
          <span>{name}</span>
          <span className={css({ color: 'content.default.level-1' })}>{id}</span>
        </span>
        <StoreAsTmpButton onClick={storeAsTmp} />
      </div>
      <BunjaInstanceValueInspector value={value} />
    </div>
  );
};
interface BunjaInstanceValueInspectorProps {
  value: unknown;
}
const BunjaInstanceValueInspector = ({ value }: BunjaInstanceValueInspectorProps) => {
  if (!value || typeof value !== 'object') {
    return (
      <div className={css({ p: 8, rounded: 4 })} style={{ background: chromeDark.BASE_BACKGROUND_COLOR }}>
        <Inspector theme='chromeDark' table={false} data={value} />
      </div>
    );
  }
  const entries = Object.entries(value as Record<string, unknown>);
  return (
    <div className={stack({ gap: 8, p: 8, rounded: 4 })} style={{ background: chromeDark.BASE_BACKGROUND_COLOR }}>
      {entries.map((entry) =>
        entry[0].endsWith('Atom') ? (
          <AtomBivEntry key={entry[0]} entry={entry} />
        ) : (
          <PlainBivEntry key={entry[0]} entry={entry} />
        ),
      )}
    </div>
  );
};
interface BivEntryProps {
  entry: [string, unknown];
}
const AtomBivEntry = ({ entry }: BivEntryProps) => {
  const [key, atom] = entry as [string, Atom<unknown>];
  const readable = 'read' in atom;
  const writable = 'write' in atom;
  const readonly = !writable && readable;
  const writeonly = writable && !readable;
  // oxlint-disable-next-line
  const value = readable ? useAtomValue(atom) : useSetAtom(atom);
  return <PlainBivEntry entry={[`${key}${readonly ? ' (readonly)' : writeonly ? ' (writeonly)' : ''}`, value]} />;
};
const PlainBivEntry = ({ entry }: BivEntryProps) => {
  const [key, value] = entry;
  const storeAsTmp = getStoreAsTmpFn(key, value);
  return (
    <div key={key} className={stack({ gap: 4 })}>
      <div className={flex({ gap: 12, alignItems: 'center' })}>
        <span>{key}</span>
        <StoreAsTmpButton onClick={storeAsTmp} />
      </div>
      <div className={css({ ml: 8 })}>
        <Inspector theme='chromeDark' table={false} data={value} />
      </div>
    </div>
  );
};

interface StoreAsTmpButtonProps {
  onClick: () => void;
}
const StoreAsTmpButton = ({ onClick }: StoreAsTmpButtonProps) => {
  return (
    <button
      onClick={onClick}
      className={css({
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'border.default.level-2',
        bgColor: 'background.default.level-0',
        px: 6,
        py: 2,
        rounded: 3,
        textStyle: 'Text/XS/Monospace/Regular',
        color: 'content.default.level-4',
        transition: 'all 0.15s ease',
        _hover: {
          bgColor: 'background.default.level-2',
          borderColor: 'border.default.level-3',
          color: 'content.default.level-5',
        },
      })}
    >
      Store as{' '}
      <code
        className={css({
          textStyle: 'Text/XS/Monospace/Regular',
          color: 'content.accent.info',
        })}
      >
        window.tmp
      </code>
    </button>
  );
};

function getStoreAsTmpFn(name: string, value: unknown) {
  return function storeAsTmp() {
    // @ts-ignore
    window.tmp = value;
    snackbar.success({ message: `Stored ${name} as window.tmp` });
  };
}
